// src/components/MainBox.js
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import "../commonLogin.css";
import Login from "../Login/index";
import OtpAuth from "../OtpAuth";
import ForgotPassword from "../ForgotPassword/index";
import Success from "../Success/index";
import UpdatePassword from "../UpdatePassword/index";
import ChangePassword from "../ChangePassword";
import Register from "../Register";
import Homepage from "../../components/home/Homepage";
import { Box, Paper, Typography, Container, Grid, Fade } from "@mui/material";

const MainBox = () => {
  const location = useLocation();
  const [highlightedLink, setHighlightedLink] = useState(0);

  const links = ["GoStandOut", "GoLearn", "GoToTest", "GoSolveCode"];

  const handleLinkClick = (index) => {
    setHighlightedLink(index);
  };

  // Get the component to render based on the current path
  const getComponent = () => {
    switch (location.pathname) {
      case "/register":
        return <Register />;
      case "/login":
        return <Login />;
      case "/forgotPassword":
        return <ForgotPassword />;
      case "/otpVerification":
        return <OtpAuth />;
      case "/updatePassword":
        return <UpdatePassword />;
      case "/success":
        return <Success />;
      case "/changePassword":
        return <ChangePassword />;
      case "/":
      default:
        return <Homepage />;
    }
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa, #e4e8eb)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: { xs: 2, md: 4 }
        }}
      >
        <Paper
          elevation={12}
          sx={{
            width: "100%",
            maxWidth: "1200px",
            overflow: "hidden",
            borderRadius: "24px",
            background: "white",
            boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
          }}
        >
          <Grid container>
            {/* Left Side - Content */}
            <Grid 
              item 
              xs={12} 
              md={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                padding: { xs: 4, md: 8 },
                position: "relative",
                overflow: "hidden",
                zIndex: 1,
              }}
            >
              {/* Decorative elements */}
              <Box
                sx={{
                  position: "absolute",
                  width: "300px",
                  height: "300px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(0, 224, 255, 0.1), rgba(0, 106, 113, 0.05))",
                  top: "-100px",
                  left: "-100px",
                  zIndex: -1,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(0, 224, 255, 0.08), rgba(0, 106, 113, 0.03))",
                  bottom: "-50px",
                  right: "20px",
                  zIndex: -1,
                }}
              />
              
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h3" 
                    className="gradient-text-welcome"
                    sx={{
                      fontWeight: 700,
                      marginBottom: 3,
                      backgroundImage: "linear-gradient(135deg, #006A71, #00BFFF)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textShadow: "0 2px 5px rgba(0,0,0,0.1)",
                      lineHeight: 1.2
                    }}
                  >
                    Welcome to Strategic Hire
                  </Typography>

                  <Typography 
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      color: "#555",
                      marginBottom: 4,
                      maxWidth: "90%",
                      lineHeight: 1.6
                    }}
                  >
                    Transforming Job Searches into Success Stories - Because Your Resume
                    Deserves Intelligence.
                  </Typography>

                  {/* Feature points */}
                  <Box sx={{ mt: 4 }}>
                    <img src="/assests/images/loginimage.png" alt="Front" style={{width:"500px"}} />
                  </Box>
                </Box>
              </Fade>
            </Grid>

            {/* Right Side - Form */}
            <Grid 
              item 
              xs={12} 
              md={6}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #004d4d, #002626)",
                borderRadius: { xs: "0", md: "20px 20px 20px 80px" },
                position: "relative",
                overflow: "hidden",
                minHeight: { xs: "500px", md: "700px" }
              }}
            >
              {/* Background elements */}
              <Box
                sx={{
                  position: "absolute",
                  width: "300px",
                  height: "300px",
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(0, 224, 255, 0.1) 0%, rgba(0, 0, 0, 0) 70%)",
                  top: "10%",
                  right: "-100px",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(0, 224, 255, 0.08) 0%, rgba(0, 0, 0, 0) 70%)",
                  bottom: "5%",
                  left: "10%",
                }}
              />
              
              {/* Decorative lines */}
              {[45, 135, 225, 315].map((angle, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    width: "150px",
                    height: "2px",
                    background: "linear-gradient(90deg, rgba(0, 224, 255, 0.2), rgba(0, 0, 0, 0))",
                    transform: `rotate(${angle}deg)`,
                    top: ["20%", "70%", "30%", "80%"][index],
                    left: ["70%", "20%", "80%", "30%"][index],
                  }}
                />
              ))}
              
              {/* Forms container */}
              <Box 
                sx={{ 
                  width: "100%", 
                  maxWidth: "420px",
                  padding: { xs: 3, sm: 4 },
                  zIndex: 2
                }}
              >
                {getComponent()}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default MainBox;